/**
 * Local (IndexedDB) persistence of an in-progress inspection so a dropped signal,
 * reload, or crash never loses work. The pure reconcile logic is exported and
 * unit-tested; the rest is thin IDB I/O via lib/idb.
 */
import { STORE_DRAFTS, idbGet, idbGetAll, idbPut, idbDelete } from './idb';
import type { PhotoGeoMap } from './photoLocation';

// Structurally compatible with the (locally-declared) inspection shapes in
// InspectionReportWorkflow.tsx — the workflow passes its arrays straight in.
//
// `photo_geo` maps a photo's URL (or its local-photo:// marker while queued) to
// where it was taken. Optional throughout: drafts written by an earlier build
// are already sitting in IndexedDB without it, and a photo whose location could
// not be resolved simply has no entry.
export interface ControllerInspection {
  controller_id: string;
  failure_codes: string[];
  notes: string;
  photo_urls: string[];
  photo_geo?: PhotoGeoMap;
}
export interface DoubleCheckInspection {
  doublecheck_id: string;
  failure_codes: string[];
  notes: string;
  photo_urls: string[];
  photo_geo?: PhotoGeoMap;
}
export interface ZoneIssueEntry {
  tempId: string;
  work_code: string;
  notes: string;
  photo_urls: string[];
  photo_geo?: PhotoGeoMap;
}
export interface ZoneInspection {
  zone_id: string;
  issues: ZoneIssueEntry[];
}

/**
 * Reference data the server-push needs (controller/zone/double-check metadata +
 * the draft/submitted status ids). Stored ON the draft so a background sync can
 * push it without the workflow being mounted.
 */
export interface DraftRefData {
  controllers: {
    id: string;
    controller_number: number;
    name: string;
    location: string | null;
    zones: { id: string; zone_number: number; type: string | null }[];
  }[];
  doubleChecks: { id: string; description: string; size: number | string | null }[];
  draftStatusId: string;
  submittedStatusId: string;
}

export interface LocalDraft {
  /** Deterministic per property+month: `inspection_${jobSiteId}_${month}`. */
  localId: string;
  serverReportId: string | null;
  jobSiteId: string;
  jobSiteName: string;
  month: string;
  aspireWorkTicketId: number | null;
  aspireWorkTicketNumber: number | null;
  controllerInspections: ControllerInspection[];
  doublecheckInspections: DoubleCheckInspection[];
  zoneInspections: ZoneInspection[];
  refData: DraftRefData | null;
  updatedAt: number;
  /** Has unsynced local edits. */
  dirty: boolean;
  /**
   * The tech tapped Submit (not just Save Draft) while this draft was last
   * persisted. The background sync must therefore push it as a SUBMITTED report
   * (flip is_draft=false) on reconnect, not silently re-sync it as a draft.
   * A subsequent explicit Save Draft clears it back to false.
   */
  pendingSubmit: boolean;
  lastSyncedAt: number | null;
  /**
   * Consecutive failed (non-403) background-sync attempts. Reset to 0 on a
   * successful sync or any fresh user edit. Optional for back-compat with drafts
   * stored before this field existed (treat absent as 0).
   */
  syncAttempts?: number;
  /**
   * Auto-sync has given up on this draft after too many consecutive failures
   * (e.g. the property/equipment it references changed server-side, so the push
   * can never succeed). It is excluded from automatic retries and surfaced in the
   * connection indicator until the user retries or discards it. Absent = false.
   */
  syncBlocked?: boolean;
  /** Last sync-failure message, shown to the user. Absent/null = none. */
  lastSyncError?: string | null;
}

/** Consecutive non-403 failures after which auto-sync stops retrying a draft. */
export const MAX_SYNC_ATTEMPTS = 5;

export function localIdFor(jobSiteId: string, month: string): string {
  return `inspection_${jobSiteId}_${month}`;
}

export type LocalDraftPatch = Omit<
  LocalDraft,
  'updatedAt' | 'dirty' | 'lastSyncedAt' | 'pendingSubmit' | 'syncAttempts' | 'syncBlocked' | 'lastSyncError'
> &
  Partial<Pick<LocalDraft, 'dirty' | 'lastSyncedAt' | 'pendingSubmit'>>;

/** Upsert a local draft, stamping updatedAt and marking dirty (unless overridden). */
export async function saveLocalDraft(patch: LocalDraftPatch): Promise<LocalDraft> {
  const existing = await loadLocalDraft(patch.localId);
  const draft: LocalDraft = {
    lastSyncedAt: existing?.lastSyncedAt ?? null,
    ...patch,
    updatedAt: Date.now(),
    dirty: patch.dirty ?? true,
    // Preserve a prior Submit intent across silent autosaves (which omit the
    // field); only an explicit Save Draft / Submit passes it to change it.
    pendingSubmit: patch.pendingSubmit ?? existing?.pendingSubmit ?? false,
    // A fresh save is fresh sync intent: clear any prior failure/block so the new
    // state is retried from scratch (this is also the implicit "retry" when a user
    // re-opens and edits a draft that auto-sync had given up on).
    syncAttempts: 0,
    syncBlocked: false,
    lastSyncError: null,
  };
  await idbPut(STORE_DRAFTS, draft);
  return draft;
}

export function loadLocalDraft(localId: string): Promise<LocalDraft | null> {
  return idbGet<LocalDraft>(STORE_DRAFTS, localId);
}

export function getAllLocalDrafts(): Promise<LocalDraft[]> {
  return idbGetAll<LocalDraft>(STORE_DRAFTS);
}

export async function loadLocalDraftByServerId(serverReportId: string): Promise<LocalDraft | null> {
  const all = await getAllLocalDrafts();
  return all.find((d) => d.serverReportId === serverReportId) ?? null;
}

/** Record a successful server sync: link the server id, clear dirty, stamp synced. */
export async function markSynced(localId: string, serverReportId: string): Promise<void> {
  const draft = await loadLocalDraft(localId);
  if (!draft) return;
  await idbPut(STORE_DRAFTS, {
    ...draft,
    serverReportId,
    dirty: false,
    lastSyncedAt: Date.now(),
    syncAttempts: 0,
    syncBlocked: false,
    lastSyncError: null,
  });
}

/**
 * Record a failed (non-403) background push. Increments the attempt counter and,
 * once it reaches MAX_SYNC_ATTEMPTS, blocks the draft from further automatic
 * retries (it's surfaced for manual retry/discard instead of looping silently
 * forever). Returns the updated draft, or null if it's gone.
 */
export async function markSyncFailure(localId: string, message: string): Promise<LocalDraft | null> {
  const draft = await loadLocalDraft(localId);
  if (!draft) return null;
  const syncAttempts = (draft.syncAttempts ?? 0) + 1;
  const updated: LocalDraft = {
    ...draft,
    syncAttempts,
    syncBlocked: syncAttempts >= MAX_SYNC_ATTEMPTS,
    lastSyncError: message,
  };
  await idbPut(STORE_DRAFTS, updated);
  return updated;
}

/** Clear a draft's sync block + failure count so the next sync retries it. */
export async function clearSyncBlock(localId: string): Promise<void> {
  const draft = await loadLocalDraft(localId);
  if (!draft) return;
  await idbPut(STORE_DRAFTS, { ...draft, syncAttempts: 0, syncBlocked: false, lastSyncError: null });
}

export function deleteLocalDraft(localId: string): Promise<void> {
  return idbDelete(STORE_DRAFTS, localId);
}

/**
 * Pure: which copy should hydrate the workflow on open? Newer wins; a dirty
 * local that is at least as new as the server beats the server copy.
 */
export function chooseDraftSource(
  localUpdatedAt: number | null,
  serverUpdatedAt: number | null,
  localDirty: boolean,
): 'local' | 'server' | 'none' {
  if (localUpdatedAt == null && serverUpdatedAt == null) return 'none';
  if (localUpdatedAt == null) return 'server';
  if (serverUpdatedAt == null) return 'local';
  if (localUpdatedAt > serverUpdatedAt) return 'local';
  if (localUpdatedAt === serverUpdatedAt) return localDirty ? 'local' : 'server';
  return 'server';
}
